import {
	Card,
	Divider,
	Group,
	SimpleGrid,
	Skeleton,
	Stack,
	Text,
	ThemeIcon,
	Title,
} from "@mantine/core";
import { Baby, BarChart3, TrendingDown, Users } from "lucide-react";
import { useIsDark } from "@/hooks/useIsDark";
import { useTranslate } from "@/hooks/useTranslate";
import { CHART } from "@/theme";
import {
	calcDynamicsNet,
	formatSigned,
	trendOf,
} from "./demografi-dynamics.calc";
import { NET_HINT_FONT_SIZE, NET_ROW_GAP } from "./demografi-layout.const";

interface DemografiDynamicsCardProps {
	births: number;
	deaths: number;
	moveIn: number;
	moveOut: number;
	loading: boolean;
}

export const DemografiDynamicsCard = ({
	births,
	deaths,
	moveIn,
	moveOut,
	loading,
}: DemografiDynamicsCardProps) => {
	const t = useTranslate();
	const dark = useIsDark();

	const net = calcDynamicsNet({ births, deaths, moveIn, moveOut });
	const trendColor = (value: number) => {
		const trend = trendOf(value);
		if (trend === "naik") return CHART.green;
		if (trend === "turun") return CHART.red;
		return dark ? "gray.4" : "gray.6";
	};
	const netRows = [
		{
			label: t.demografiPekerjaan.pertumbuhanAlami,
			hint: t.demografiPekerjaan.pertumbuhanAlamiKeterangan,
			value: net.natural,
		},
		{
			label: t.demografiPekerjaan.migrasiBersih,
			hint: t.demografiPekerjaan.migrasiBersihKeterangan,
			value: net.migration,
		},
	];

	// Dynamic Stats Data
	const dynamicStats = [
		{
			title: t.demografiPekerjaan.kelahiran,
			value: births.toString(),
			icon: Baby,
			color: CHART.green,
		},
		{
			title: t.demografiPekerjaan.kematian,
			value: deaths.toString(),
			icon: TrendingDown,
			color: CHART.red,
		},
		{
			title: t.demografiPekerjaan.pindahMasuk,
			value: moveIn.toString(),
			icon: Users,
			color: CHART.blue,
		},
		{
			title: t.demografiPekerjaan.pindahKeluar,
			value: moveOut.toString(),
			icon: Users,
			color: CHART.orange,
		},
	];

	return (
		<Card
			data-ai-target="demografi.dinamika"
			p="md"
			radius="xl"
			withBorder
			bg={dark ? "#1E293B" : "white"}
			style={{
				borderColor: dark ? "#334155" : "white",
				boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
				display: "flex",
				flexDirection: "column",
			}}
			h="100%"
		>
			<Group gap="xs" mb="md">
				<ThemeIcon
					color="darmasaba-navy.7"
					variant="filled"
					size="sm"
					radius="sm"
				>
					<BarChart3 size={14} />
				</ThemeIcon>
				<Title order={4} c={dark ? "white" : "gray.9"}>
					{t.demografiPekerjaan.dinamikaPenduduk}
				</Title>
			</Group>
			{loading ? (
				<Skeleton style={{ flex: 1 }} radius="md" />
			) : (
				<>
					{/*
					  SimpleGrid, bukan Grid: Grid membungkus kolomnya dengan flex-wrap,
					  dan `align` hanya mengatur tinggi dalam satu baris (`align-items`),
					  bukan pembagian antar baris (`align-content`) — sehingga kedua baris
					  menggumpal di atas dan menyisakan ruang kosong. SimpleGrid memakai
					  CSS Grid, jadi `1fr 1fr` benar-benar membagi tinggi yang tersedia.
					*/}
					<SimpleGrid
						cols={2}
						spacing="sm"
						style={{ flex: 1, gridTemplateRows: "1fr 1fr" }}
					>
						{dynamicStats.map((stat) => (
							<Card
								key={stat.title}
								p="sm"
								radius="lg"
								bg={dark ? "#334155" : "#F1F5F9"}
								h="100%"
								style={{
									transition: "transform 0.15s ease",
									cursor: "pointer",
								}}
							>
								<Stack gap={2} align="center" justify="center" h="100%">
									<ThemeIcon
										color={stat.color}
										variant="filled"
										size="md"
										radius="lg"
									>
										<stat.icon size={14} />
									</ThemeIcon>
									<Text size="xs" c="dimmed" ta="center">
										{stat.title}
									</Text>
									<Text
										size="lg"
										fw={700}
										c={stat.color}
										style={{ lineHeight: 1 }}
									>
										{stat.value}
									</Text>
								</Stack>
							</Card>
						))}
					</SimpleGrid>
					{/*
					  Empat angka di atas hanya bercerita setengah: yang menentukan
					  penduduk bertambah atau berkurang adalah selisihnya. Semua angka
					  di sini turunan dari keempat angka itu — tidak ada data tambahan.
					*/}
					<Stack gap={NET_ROW_GAP} mt="md">
						{netRows.map((row) => (
							<Group
								key={row.label}
								justify="space-between"
								align="baseline"
								wrap="nowrap"
							>
								<Stack gap={0}>
									<Text size="xs" c={dark ? "gray.3" : "gray.7"}>
										{row.label}
									</Text>
									{row.hint && (
										<Text fz={NET_HINT_FONT_SIZE} c="dimmed">
											{row.hint}
										</Text>
									)}
								</Stack>
								<Text size="sm" fw={700} c={trendColor(row.value)}>
									{formatSigned(row.value)}
								</Text>
							</Group>
						))}
						<Divider my={0} color={dark ? "#334155" : "#e5e7eb"} />
						<Group justify="space-between" align="baseline" wrap="nowrap">
							<Text size="sm" fw={600} c={dark ? "white" : "gray.9"}>
								{t.demografiPekerjaan.perubahanTotal}
							</Text>
							<Text size="lg" fw={700} c={trendColor(net.total)}>
								{formatSigned(net.total)}
							</Text>
						</Group>
					</Stack>
				</>
			)}
		</Card>
	);
};
